<div class="data-source-profile-layout">
  <!-- Main content area with content-wrapper -->
  <div class="content-wrapper-parent">
    <div class="content-wrapper">
      <div class="data-source-profile-container">
        <!-- Loading state -->
        <div *ngIf="loading" class="loading-container">
          <mat-spinner></mat-spinner>
          <p class="loading-text">{{ loadingMessage }}</p>
          <div class="loading-progress-bar-container">
            <!-- Show indeterminate during upload preparation -->
            <mat-progress-bar 
              *ngIf="uploadProgress === 0 && totalBytes > 0" 
              mode="indeterminate">
            </mat-progress-bar>
            <span *ngIf="uploadProgress === 0 && totalBytes > 0" class="loading-step-text">
              Preparing {{ getFileSizeString(totalBytes) }}...
            </span>
            <!-- Show upload progress when uploading -->
            <mat-progress-bar 
              *ngIf="uploadProgress > 0 && uploadProgress < 100" 
              mode="determinate" 
              [value]="uploadProgress">
            </mat-progress-bar>
            <span *ngIf="uploadProgress > 0 && uploadProgress < 100" class="loading-step-text">
              {{ uploadStatusText }}: {{ getFileSizeString(uploadedBytes) }} of {{ getFileSizeString(totalBytes) }}
            </span>
            <!-- Show indeterminate during processing after upload -->
            <mat-progress-bar 
              *ngIf="uploadProgress === 100 && !profilingTotalSteps" 
              mode="indeterminate">
            </mat-progress-bar>
            <span *ngIf="uploadProgress === 100 && !profilingTotalSteps" class="loading-step-text">{{ uploadStatusText }}</span>
            <!-- Show profiling progress -->
            <mat-progress-bar 
              *ngIf="profilingTotalSteps && (profilingCurrentStep ?? 0) === 0" 
              mode="indeterminate">
            </mat-progress-bar>
            <span *ngIf="profilingTotalSteps && (profilingCurrentStep ?? 0) === 0" class="loading-step-text">
              {{ uploadStatusText }}
            </span>
            <mat-progress-bar 
              *ngIf="profilingTotalSteps && (profilingCurrentStep ?? 0) > 0" 
              mode="determinate" 
              [value]="((profilingCurrentStep ?? 0) / profilingTotalSteps) * 100">
            </mat-progress-bar>
            <span *ngIf="((profilingCurrentStep ?? 0) > 0 && profilingTotalSteps) || fetchingProfilingResults" class="loading-step-text">
              {{ fetchingProfilingResults ? 'Fetching Results' : ('Step ' + (profilingCurrentStep ?? 0) + ' of ' + profilingTotalSteps) }}
            </span>
          </div>
        </div>

        <!-- Main content -->
        <div *ngIf="!loading" class="content-container">
          <!-- Assessment setup shown before the assessment starts -->
          <div *ngIf="showConfig" class="assessment-configuration">
            <section class="assessment-input-panel">
              <div class="assessment-section-heading">
                <div>
                  <h2>Assessment Inputs</h2>
                  <p class="subtitle">Choose the data sources to include in this assessment</p>
                </div>
              </div>

              <div class="assessment-input-list">
                <div *ngFor="let input of assessmentInputs" class="assessment-input-item">
                  <div class="assessment-input-card">
                    <mat-icon [svgIcon]="input.type === 'file' ? 'upload_file' : 'database'"></mat-icon>
                    <div class="assessment-input-details">
                      <span class="assessment-input-name">{{ input.name }}</span>
                    </div>
                        <button *ngIf="input.type === 'datasource' && assessmentInputs.length > 1" mat-icon-button type="button"
                            [attr.aria-label]="'Remove ' + input.name"
                            (click)="removeAssessmentInput(input.id)">
                      <mat-icon svgIcon="close"></mat-icon>
                    </button>
                  </div>
                </div>

                <app-data-source-picker
                  style="padding-top:6px"
                  *ngIf="assessmentInputs.length > 0"
                  [dataSources]="assessmentDataSources"
                  [selectedDataSourceIds]="assessmentDataSourceIds"
                  [allowMultipleSelection]="true"
                  [allowUploadFile]="true"
                  [allowDataSourceSelection]="true"
                  [isLoadingDataSources]="isLoadingAssessmentDataSources"
                  (uploadFile)="uploadAssessmentFile()"
                  (selectExisting)="selectExistingAssessmentDataSource()"
                  (createNew)="createAssessmentDataSource()"
                  (dataSourceSelected)="selectAssessmentDataSource($event)">
                </app-data-source-picker>
              </div>
            </section>

            <section class="assessment-options-panel">
              <div class="assessment-section-heading">
                <div>
                  <h2>Assessment Options</h2>
                  <p class="subtitle">Select which parts of the assessment to run</p>
                </div>
                <div class="view-mode-toggle">
                  <button
                    class="view-mode-toggle-btn"
                    [class.active]="layoutService.getViewMode() === 'simple'"
                    (click)="layoutService.setViewMode('simple')">
                    Simple
                  </button>
                  <button
                    class="view-mode-toggle-btn"
                    [class.active]="layoutService.getViewMode() === 'advanced'"
                    (click)="layoutService.setViewMode('advanced')">
                    Advanced
                  </button>
                </div>
              </div>
              <div class="assessment-option-list items-content">
                <div class="items-grid">
                  <div *ngFor="let opt of configOptions"
                       class="card-selectable task-card assessment-option-card"
                       [class.selected]="selectedConfigIds.has(opt.id)"
                       role="button"
                       tabindex="0"
                       (click)="toggleConfig(opt.id, !selectedConfigIds.has(opt.id))"
                       (keydown.enter)="toggleConfig(opt.id, !selectedConfigIds.has(opt.id))"
                       (keydown.space)="$event.preventDefault(); toggleConfig(opt.id, !selectedConfigIds.has(opt.id))">
                    <div class="item-header">
                      <mat-icon class="item-icon assessment-option-icon">tune</mat-icon>
                      <div class="item-info">
                        <h5>{{ opt.label }}</h5>
                        <span class="item-type">{{ selectedConfigIds.has(opt.id) ? 'Enabled' : 'Disabled' }}</span>
                      </div>
                      <div class="item-header-actions">
                        <mat-checkbox [checked]="selectedConfigIds.has(opt.id)"
                                      (click)="$event.stopPropagation()"
                                      (change)="toggleConfig(opt.id, $event.checked)"
                                      [aria-label]="'Enable ' + opt.label"></mat-checkbox>
                      </div>
                    </div>
                    <p class="item-description">{{ getConfigDescription(opt) }}</p>
                  </div>
                </div>
              </div>
            </section>

          </div>

          <!-- Header Section -->
          <div class="header-section" *ngIf="!showConfig">
            <div class="header-content">
              <div class="title-section">
                <div class="title-with-action">
                  <h1>{{ selectedAssessmentTable?.name || profileName || dataSource?.dataSourceName || 'Data Source' }}</h1>
                  <button mat-icon-button class="header-action-btn"
                          *ngIf="dataProfileRunId"
                          (click)="editProfileName()"
                          matTooltip="Rename profile"
                          aria-label="Rename data profile">
                    <mat-icon svgIcon="pencil"></mat-icon>
                  </button>
                </div>
                <p class="subtitle" *ngIf="hasTableBasicStats">{{ numberOfRows | number }} rows, {{ numberOfColumns }} columns{{ fileSize ? ', ' + fileSize : '' }}</p>
              </div>
              <!-- View Mode Toggle -->
              <div class="view-mode-toggle">
                <button
                  class="view-mode-toggle-btn"
                  [class.active]="layoutService.getViewMode() === 'simple'"
                  (click)="layoutService.setViewMode('simple')">
                  Simple
                </button>
                <button
                  class="view-mode-toggle-btn"
                  [class.active]="layoutService.getViewMode() === 'advanced'"
                  (click)="layoutService.setViewMode('advanced')">
                  Advanced
                </button>
              </div>
              <div *ngIf="isProfilingUploadedFile() && !isSavingProfile" class="create-datasource-container">
                <button mat-raised-button 
                        class="primary-button create-datasource-btn"
                        (click)="createDataSourceFromFile()">
                  <mat-icon svgIcon="upload_file_white"></mat-icon>
                  Create Data Source
                </button>
                <mat-icon class="info-icon" svgIcon="info-circle"
                  matTooltip="Save this file as a reusable data source for future projects"></mat-icon>
              </div>
              <div *ngIf="isSavingProfile" class="saving-profile-indicator">
                <mat-spinner diameter="24"></mat-spinner>
                <span class="saving-text">Saving Data Assessment...</span>
              </div>
            </div>
          </div>

          <!-- Select the datasource whose results are shown below. -->
          <section class="assessment-table-navigation" *ngIf="!showConfig && assessmentTables.length > 1">
            <div class="assessment-table-navigation-heading">
              <div>
                <span class="eyebrow">Data Assessment</span>
                <h2>Assessment results</h2>
              </div>
              <span class="table-count">{{ assessmentTables.length }} data sources</span>
            </div>
            <div class="assessment-table-list" role="tablist" aria-label="Assessment results by data source">
              <button
                *ngFor="let table of assessmentTables"
                type="button"
                class="assessment-table-tab"
                [class.active]="table.id === selectedAssessmentTableId"
                role="tab"
                [attr.aria-selected]="table.id === selectedAssessmentTableId"
                (click)="selectAssessmentTable(table.id)">
                <mat-icon svgIcon="table_chart"></mat-icon>
                <span class="assessment-table-tab-content">
                  <strong>{{ table.name }}</strong>
                  <small>{{ table.id }}</small>
                </span>
                <mat-icon class="selected-table-icon" *ngIf="table.id === selectedAssessmentTableId" svgIcon="check"></mat-icon>
              </button>
            </div>
          </section>

          <!-- Error state -->
          <div *ngIf="!showConfig && error" class="error-go-back">
            <button mat-button (click)="goBack()">Go Back</button>
          </div>

          <!-- Column Cards Section (Stacked Layout) -->
          <div class="columns-section" *ngIf="!showConfig && !error">
            <div class="columns-scroll-container">
              
              <!-- Data Summary Section -->
              <div class="summary-section" *ngIf="hasSummaryData && dataSummary">
                <app-data-summary [summary]="dataSummary" [title]="'Data Overview'" [expanded]="true"></app-data-summary>
                <div class="help-text-section" *ngIf="tableFields && tableFields.length > 0">
                  <mat-icon class="info-icon" svgIcon="info-circle"></mat-icon>
                  <div>Click a column below to view more details, including statistics, missing value analysis, and visualisations</div>
                </div>
              </div>

              <div class="profile-empty-state" *ngIf="!hasColumnBasicStats && !dataSummary">
                <mat-icon svgIcon="info-circle"></mat-icon>
                <p>{{ getMainContentAvailabilityMessage() }}</p>
              </div>

              <!-- Column Card for each field -->
              <ng-container *ngIf="hasColumnBasicStats">
                <div *ngFor="let field of tableFields" class="column-card-row">
                
                <!-- Combined Card with Two Sections -->
                <div class="column-card" 
                     [class.selected]="isColumnSelected(field.field)"
                     (click)="onColumnHeaderClick($event, field.field)"
                     role="button"
                     tabindex="0"
                     [attr.aria-pressed]="isColumnSelected(field.field)">
                  
                  <!-- Left Section: Column Metadata -->
                  <div class="card-left-section">
                    
                    <!-- Column Name and Type Header -->
                    <div class="card-header">
                      <div class="column-title-row">
                        <img 
                          class="data-category-icon"
                          [src]="getDataCategoryIcon(field.dataCategory)"
                          [alt]="getDataCategoryDisplayName(field.dataCategory)"
                          [matTooltip]="getDataCategoryDisplayName(field.dataCategory)"
                          matTooltipPosition="right" />
                        <div class="column-name-type">
                          <h3 class="column-name">{{ field.field }}</h3>
                          <div class="category-type-text">
                            {{ getDataCategoryDisplayName(field.dataCategory) }}<span *ngIf="layoutService.isAdvancedMode()" class="column-type"> ({{ field.dataType }})</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Column Statistics -->
                    <div class="card-stats">
                      <div class="stat-item">
                        <span class="stat-label">Missing:</span>
                        <span class="stat-value">{{ field.missing }} ({{ (field.nullPercentage).toFixed(2) }}%)</span>
                      </div>
                      <div class="stat-item">
                        <span class="stat-label">Unique:</span>
                        <span class="stat-value">{{ field.unique }} ({{ (field.distinctPercentage).toFixed(2) }}%)</span>
                      </div>
                      <div *ngIf="field.minimum !== undefined && field.minimum !== 'N/A' && (!field.distribution || field.distribution.length === 0)" class="stat-item">
                        <span class="stat-label">{{ isNumericType(field.dataType) ? 'Min:' : 'Min Length:' }}</span>
                        <span class="stat-value">{{ field.minimum }}</span>
                      </div>
                      <div *ngIf="field.maximum !== undefined && field.maximum !== 'N/A' && (!field.distribution || field.distribution.length === 0)" class="stat-item">
                        <span class="stat-label">{{ isNumericType(field.dataType) ? 'Max:' : 'Max Length:' }}</span>
                        <span class="stat-value">{{ field.maximum }}</span>
                      </div>
                    </div>

                    <!-- Distribution Chart (if available AND has a chart type) -->
                    <div *ngIf="field.distribution && field.distribution.length > 0 && (isNumericType(field.dataType) || isDateTimeType(field.dataType) || field.dataType === 'boolean')" class="card-distribution">
                      <!-- Numeric Distribution Chart -->
                      <div *ngIf="isNumericType(field.dataType)" class="distribution-bars">
                      <div 
                        *ngFor="let value of field.distribution; let i = index" 
                        class="distribution-bar"
                        [style.height.%]="getDistributionHeight(value, field.distribution!)"
                        [matTooltip]="getRangeLabel(i, field) + ': ' + value + ' values'"
                        matTooltipPosition="above">
                      </div>
                    </div>
                    
                    <!-- Date/DateTime Distribution Chart -->
                    <div *ngIf="isDateTimeType(field.dataType)" class="distribution-bars date-bars">
                      <div 
                        *ngFor="let value of field.distribution; let i = index" 
                        class="distribution-bar date-bar"
                        [style.height.%]="getDistributionHeight(value, field.distribution!)"
                        [matTooltip]="(field.distributionLabels?.[i] || 'Period ' + (i + 1)) + ': ' + value + ' entries'"
                        matTooltipPosition="above">
                      </div>
                    </div>
                    
                    <!-- Boolean Distribution Chart -->
                    <div *ngIf="field.dataType === 'boolean'" class="boolean-distribution">
                      <div class="boolean-bars">
                        <div 
                          *ngFor="let value of field.distribution; let i = index" 
                          class="boolean-bar"
                          [class.true-bar]="i === 0"
                          [class.false-bar]="i === 1"
                          [class.null-bar]="i === 2"
                          [style.height.%]="getDistributionHeight(value, field.distribution!)"
                          [matTooltip]="(field.distributionLabels?.[i] || 'Value') + ': ' + value + ' count'"
                          matTooltipPosition="above">
                        </div>
                      </div>
                      <div class="boolean-labels">
                        <span *ngFor="let label of field.distributionLabels; let i = index" 
                              class="boolean-label"
                              [class.true-label]="i === 0"
                              [class.false-label]="i === 1"
                              [class.null-label]="i === 2">
                          {{ label }}
                        </span>
                      </div>
                    </div>
                    
                    <!-- Range display for numeric and date columns -->
                    <div *ngIf="isNumericType(field.dataType)" class="distribution-range">
                      <span class="range-min">Min {{ field.minimum }}</span>
                      <span class="range-max">Max {{ field.maximum }}</span>
                    </div>
                    <div *ngIf="isDateTimeType(field.dataType)" class="distribution-range">
                      <span class="range-min">Min {{ formatDateTimeValue(field.minimum) }}</span>
                      <span class="range-max">Max {{ formatDateTimeValue(field.maximum) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Vertical Divider -->
                <div class="card-divider"></div>

                <!-- Right Section: Identified Issues -->
                <div class="card-right-section">
                  <div class="issues-header">
                    <h4>Identified Issues</h4>
                    <span class="assessment-score" *ngIf="field.assessmentScore">
                      Column Score: {{ field.assessmentScore.score | number:'1.0-1' }}%
                      <span
                        class="assessment-score-dot"
                        [class]="getActiveSpeedometerSegment(field.assessmentScore.score)"
                        [attr.aria-label]="getQualityLabel(field.assessmentScore.score) + ' quality'"
                        [matTooltip]="getQualityLabel(field.assessmentScore.score)">
                      </span>
                    </span>
                  </div>
                  
                  <div class="issues-list" *ngIf="field.issues && field.issues.length > 0">
                    <div *ngFor="let issue of field.issues" class="issue-item">
                      <!-- Enabled checkbox: recommendations exist and are available -->
                      <div *ngIf="hasAvailableRecommendations(field.field, issue.id)" class="checkbox-wrapper">
                        <mat-checkbox
                          [checked]="isIssueSelected(field.field, issue.id)"
                          (change)="onIssueCheckboxChange(field.field, issue, $event.checked)"
                          color="primary">
                        </mat-checkbox>
                        <div class="checkbox-label">Fix</div>
                      </div>
                      <!-- Disabled checkbox: recommendations exist but are not available -->
                      <div *ngIf="hasUnavailableRecommendations(field.field, issue.id)" class="checkbox-wrapper">
                        <mat-checkbox
                          [checked]="false"
                          [disabled]="true"
                          color="primary"
                          matTooltip="This fix requires a goal that is not available in your plan"
                          matTooltipPosition="above">
                        </mat-checkbox>
                        <button mat-stroked-button class="upgrade-btn" (click)="onUpgradeClick(field.field, issue.id)">Upgrade</button>
                      </div>
                      <div class="issue-content" [class.no-checkbox]="!hasRecommendations(field.field, issue.id)">
                        <div class="issue-title-row">
                          <span class="issue-title">{{ issue.title }}</span>
                          <span class="severity-badge">
                            {{ issue.severity }}
                          </span>
                        </div>
                        <p class="issue-description" *ngIf="issue.description">{{ issue.description }}</p>
                        <div class="issue-stats" *ngIf="issue.affectedRows !== undefined">
                          <span class="issue-stat">{{ issue.affectedRows | number }} rows affected</span>
                          <span class="issue-stat" *ngIf="issue.affectedPercentage !== undefined">
                            ({{ issue.affectedPercentage.toFixed(1) }}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="no-issues" *ngIf="field.issueDataAvailable && (!field.issues || field.issues.length === 0)">
                    <mat-icon class="check-icon">check_circle</mat-icon>
                    <p>No issues detected</p>
                  </div>
                </div>

              </div><!-- .column-card -->
                </div><!-- .column-card-row -->
              </ng-container>

            </div>
          </div>

        </div>
      </div>

      <!-- Floating Basket Component (hidden while loading or for reopened uploaded file profiles) -->
      <app-floating-basket *ngIf="!loading && (showConfig || shouldShowBasket())"
        [mode]="showConfig ? 'configuration' : 'cleaning'"
        [items]="basketItems"
        [totalCount]="totalIssueCount"
        [selectedDatasources]="assessmentInputs.length"
        [selectedOptions]="selectedConfigIds.size"
        [creatingWorkflow]="creatingWorkflow"
        [workflowProgress]="workflowProgress"
        (viewSelected)="onViewSelectedIssues()"
        (selectAll)="onSelectAll()"
        (startWorkflow)="showConfig ? startAssessment() : onStartCleaningWorkflow()">
      </app-floating-basket>

    </div>
  </div>

  <!-- Right Sidebar (outside content-wrapper, similar to projects collection sidebar) -->
  <div class="right-sidebar" [class.collapsed]="isRightSidebarCollapsed">
    <div 
      class="sidebar-toggle-btn" 
      (click)="toggleRightSidebar()"
      [matTooltip]="isRightSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      matTooltipPosition="left">
      <mat-icon>{{ isRightSidebarCollapsed ? 'chevron_left' : 'chevron_right' }}</mat-icon>
    </div>
    
    <!-- Column Selection Dropdown -->
    <div class="sidebar-header-background">
      <mat-form-field class="column-select" appearance="fill">
        <mat-select [value]="selectedColumnName || 'table-summary'" (selectionChange)="onColumnSelectChange($event.value)">
          <mat-option value="table-summary">Table Summary</mat-option>
          <mat-option *ngFor="let field of tableFields" [value]="field.field">
            {{ field.field }}
          </mat-option>
        </mat-select>
      </mat-form-field>
    </div>
    
    <div class="sidebar-content" *ngIf="!loading && !error">
      
      <!-- Table Information (default view) -->
      <div *ngIf="!selectedColumnName" class="table-info-section">
        
        <!-- Data Quality Scores Section -->
        <div class="quality-section" *ngIf="hasFullAssessment">
          
          <!-- Main Data Quality Score - Speedometer Dial -->
          <div class="main-quality-score">
            <h3 class="quality-section-title">Overall Data Quality</h3>
            <div class="speedometer-container">
              <svg class="speedometer-svg" viewBox="0 0 200 115" preserveAspectRatio="xMidYMid meet">
                <!-- Segmented arcs with distinct colors -->
                <!-- Red segment: 0-60% -->
                <path class="gauge-arc-bg" 
                      d="M 30 100 A 70 70 0 0 1 121.6 33.4" 
                      fill="none" 
                      stroke="#ef5350" 
                      stroke-width="20"
                      stroke-linecap="butt"/>
                
                <!-- Orange segment: 60-75% -->
                <path class="gauge-arc-bg" 
                      d="M 121.6 33.4 A 70 70 0 0 1 149.5 50.5" 
                      fill="none" 
                      stroke="#ff9800" 
                      stroke-width="20"
                      stroke-linecap="butt"/>
                
                <!-- Yellow segment: 75-90% -->
                <path class="gauge-arc-bg" 
                      d="M 149.5 50.5 A 70 70 0 0 1 166.6 78.4" 
                      fill="none" 
                      stroke="#ffeb3b" 
                      stroke-width="20"
                      stroke-linecap="butt"/>
                
                <!-- Green segment: 90-100% -->
                <path class="gauge-arc-bg" 
                      d="M 166.6 78.4 A 70 70 0 0 1 170 100" 
                      fill="none" 
                      stroke="#4caf50" 
                      stroke-width="20"
                      stroke-linecap="butt"/>
                
                <!-- Percentage text at center -->
                <text class="gauge-percentage" x="100" y="100" text-anchor="middle" dominant-baseline="middle">
                  {{ overallQualityScore !== null ? Math.round(overallQualityScore) + '%' : 'N/A' }}
                </text>
                
                <!-- Needle with rounded point - using path with curves -->
                <path class="gauge-needle" 
                      d="M 98,65 L 98,40 Q 98,32 100,32 Q 102,32 102,40 L 102,65 Z"
                      fill="#333"
                      [style.transform]="'rotate(' + getSpeedometerRotation(overallQualityScore) + 'deg)'"
                      [style.transform-origin]="'100px 100px'"/>
              </svg>
            </div>
            <div class="quality-label-text">
              {{ getQualityLabel(overallQualityScore) }}
            </div>
          </div>
          
            
          
          <div class="sub-scores">
            <div class="quality-item">
              <div class="quality-row">
                <div class="quality-header">
                  <span class="quality-label">Validity</span>
                  <mat-icon class="info-icon" svgIcon="info-circle-grey" 
                    [matTooltip]="getQualityScoreTooltip('validity', validityScore)"></mat-icon>
                </div>
                <div class="progress-container">
                  <div class="progress-bar">
                    <div class="progress-fill" [style.background-color]="getScoreColor(validityScore)" [style.width.%]="validityScore ?? 0"></div>
                  </div>
                  <span class="progress-text">{{ validityScore !== null ? Math.round(validityScore) + '%' : 'N/A' }}</span>
                </div>
              </div>
            </div>
            <div class="quality-item">
              <div class="quality-row">
                <div class="quality-header">
                  <span class="quality-label">Distributional Health</span>
                  <mat-icon class="info-icon" svgIcon="info-circle-grey" 
                    [matTooltip]="getQualityScoreTooltip('distributional_health', distributionalHealthScore)"></mat-icon>
                </div>
                <div class="progress-container">
                  <div class="progress-bar">
                    <div class="progress-fill" [style.background-color]="getScoreColor(distributionalHealthScore)" [style.width.%]="distributionalHealthScore ?? 0"></div>
                  </div>
                  <span class="progress-text">{{ distributionalHealthScore !== null ? Math.round(distributionalHealthScore) + '%' : 'N/A' }}</span>
                </div>
              </div>
            </div>

            <div class="quality-item">
              <div class="quality-row">
                <div class="quality-header">
                  <span class="quality-label">Completeness</span>
                  <mat-icon class="info-icon" svgIcon="info-circle-grey" 
                    [matTooltip]="getQualityScoreTooltip('completeness', completenessScore)"></mat-icon>
                </div>
                <div class="progress-container">
                  <div class="progress-bar">
                    <div class="progress-fill" [style.background-color]="getScoreColor(completenessScore)" [style.width.%]="completenessScore ?? 0"></div>
                  </div>
                  <span class="progress-text">{{ completenessScore !== null ? Math.round(completenessScore) + '%' : 'N/A' }}</span>
                </div>
              </div>

            </div>


            
          </div>
        </div>

        <!-- Continuity gaps come from table advanced statistics. -->
        <div class="section" *ngIf="hasTableAdvancedStats && dateGaps.length > 0">
          <div class="date-gaps-chart">
            <div class="chart-header">
              <span class="chart-title">Continuity Gaps ({{ continuityDate }})</span>
              <span class="chart-subtitle">Hover over the segments for further details</span>
            </div>
            <div class="chart-bars">
              <div class="bar-item" *ngFor="let gap of dateGaps">
                <div class="bar-wrapper"
                     [matTooltip]="'Rows ' + gap.start_idx + '-' + gap.end_idx + ': ' + gap.gap_days + ' day gap (' + (getGapRatio(gap) * 100).toFixed(1) + '% density)'">
                  <div class="bar bar-base"></div>
                  <div class="bar bar-overlay"
                       [class.bar-overflow]="getGapRatio(gap) >= 1"
                       [style.height.%]="Math.min(getGapRatio(gap) * 100, 100)">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Basic Information Section -->
        <div class="info-section" *ngIf="!showConfig && (hasTableBasicStats || hasFullAssessment || hasTableIntent)">
          <h3>Basic Information</h3>
          <div class="info-row" *ngIf="hasTableBasicStats">
            <span class="label">Number of Rows:</span>
            <span class="value">{{ numberOfRows ?? 'N/A' }}</span>
          </div>
          <div class="info-row" *ngIf="hasTableBasicStats">
            <span class="label">Number of Columns:</span>
            <span class="value">{{ numberOfColumns ?? 'N/A' }}</span>
          </div>
          <div class="info-row" *ngIf="hasTableBasicStats">
            <span class="label">File Size (Memory)
              <mat-icon class="info-icon" svgIcon="info-circle-grey"
                matTooltip="This reflects the size of the data loaded into memory, which may differ from the file size on disk."
                matTooltipPosition="right"></mat-icon>
            </span>
            <span class="value">{{ fileSize ?? 'N/A' }}</span>
          </div>
          <div class="info-row" *ngIf="hasTableBasicStats">
            <span class="label">Number of Duplicates:</span>
            <span class="value">{{ numberOfDuplicates ?? 'N/A' }}</span>
          </div>
          <div class="info-row" *ngIf="predictedType !== null">
            <span class="label">Predicted Type:</span>
                <span class="value">{{ predictedType }}</span>
          </div>
          <div class="info-row" *ngIf="predictedTopic !== null">
            <span class="label">Predicted Topic:</span>
                <span class="value">{{ predictedTopic }}</span>
          </div>
          <div class="info-row" *ngIf="hasTableBasicStats || selectedPrimaryKey">
            <span class="label">Primary Key:</span>
            <span class="value primary-key">{{ primaryKey ?? 'N/A' }}</span>
          </div>
          <ng-container *ngIf="hasFullAssessment && selectedPrimaryKey as key">
            <div class="info-row">
              <span class="label">Primary Key Status:</span>
              <span class="value">{{ key.status }}</span>
            </div>
            <div class="info-row">
              <span class="label">Primary Key Confidence:</span>
              <span class="value">{{ (key.confidence * 100) | number:'1.0-1' }}%</span>
            </div>
            <div class="info-row">
              <span class="label">Unique Ratio:</span>
              <span class="value">{{ (key.unique_ratio * 100) | number:'1.0-1' }}%</span>
            </div>
          </ng-container>
          <ng-container *ngIf="hasTableIntent && tableIntent as intent">
            <div class="info-row">
              <span class="label">Table Category:</span>
              <span class="value">{{ intent.category }}</span>
            </div>
            <div class="info-row">
              <span class="label">Category Confidence:</span>
              <span class="value">{{ (intent.confidence * 100) | number:'1.0-1' }}%</span>
            </div>
            <div class="info-row table-intent-subtopics">
              <span class="label">Top Subtopics:</span>
              <span class="value">{{ intent.top_subtopics }}</span>
            </div>
            <div class="info-row">
              <span class="label">Subtopics Confidence:</span>
              <span class="value">{{ (intent.subtopics_conf * 100) | number:'1.0-1' }}%</span>
            </div>
          </ng-container>
        </div>
      </div>

      <!-- Column Detail View (when column is selected) -->
      <div *ngIf="!showConfig && selectedColumnName" class="column-detail-section">
        
        <div class="detail-body">
          <!-- Visualise Button Section -->
          <div class="section">
            <button mat-raised-button color="primary" class="visualise-button" (click)="onVisualiseClick()">
              <mat-icon svgIcon="bar_chart_white"></mat-icon>
              Visualise
            </button>
          </div>

          <!-- Main Visualization Chart -->
          <div *ngIf="(getSelectedFieldData()?.distribution && getSelectedFieldData()!.distribution!.length > 0) || (isStringType(getSelectedFieldData()!.dataType) && getTopValueCounts(getSelectedFieldData()!.valueCounts).length > 0)" class="section">
            <div class="sidebar-distribution-chart">
              
              <!-- Numeric Distribution Chart -->
              <div *ngIf="isNumericType(getSelectedFieldData()!.dataType)" class="sidebar-distribution-bars">
                <svg class="histogram-curve-overlay" viewBox="0 -10 100 110" preserveAspectRatio="none">
                  <path [attr.d]="getHistogramCurvePath(getSelectedFieldData()!.distribution!)" 
                        fill="none" 
                        stroke="rgba(25, 101, 192, 0.8)" 
                        stroke-width="1" 
                        stroke-linecap="round"
                        stroke-linejoin="round"/>
                </svg>
                <div 
                  *ngFor="let value of getSelectedFieldData()!.distribution; let i = index" 
                  class="sidebar-distribution-bar"
                  [style.height.%]="getDistributionHeight(value, getSelectedFieldData()!.distribution!)"
                  [matTooltip]="getRangeLabel(i, getSelectedFieldData()!) + ': ' + (value | number) + ' values'"
                  matTooltipPosition="above">
                </div>
              </div>
              
              <!-- Date/DateTime Distribution Chart -->
              <div *ngIf="isDateTimeType(getSelectedFieldData()!.dataType)" class="sidebar-distribution-bars date-bars">
                <svg class="histogram-curve-overlay" viewBox="0 -10 100 110" preserveAspectRatio="none">
                  <path [attr.d]="getHistogramCurvePath(getSelectedFieldData()!.distribution!)" 
                        fill="none" 
                        stroke="rgba(25, 101, 192, 0.8)" 
                        stroke-width="1" 
                        stroke-linecap="round"
                        stroke-linejoin="round"/>
                </svg>
                <div 
                  *ngFor="let value of getSelectedFieldData()!.distribution; let i = index" 
                  class="sidebar-distribution-bar"
                  [style.height.%]="getDistributionHeight(value, getSelectedFieldData()!.distribution!)"
                  [matTooltip]="(getSelectedFieldData()!.distributionLabels?.[i] || 'Period ' + (i + 1)) + ': ' + (value | number) + ' entries'"
                  matTooltipPosition="above">
                </div>
              </div>
              
              <!-- Boolean Distribution Chart -->
              <div *ngIf="getSelectedFieldData()!.dataType === 'boolean'" class="boolean-distribution">
                <div *ngIf="getSelectedFieldData()!.distribution![0] > 0" 
                     class="boolean-bar true-bar" 
                     [style.height.%]="getDistributionHeight(getSelectedFieldData()!.distribution![0], getSelectedFieldData()!.distribution!)"
                     [matTooltip]="'True: ' + (getSelectedFieldData()!.distribution![0] | number) + ' values'">
                  <span *ngIf="!isBooleanBarShort(getSelectedFieldData()!.distribution![0], getSelectedFieldData()!.distribution!)" class="bar-label">T</span>
                  <span *ngIf="isBooleanBarShort(getSelectedFieldData()!.distribution![0], getSelectedFieldData()!.distribution!)" class="bar-label-above true-label">T</span>
                </div>
                <div *ngIf="getSelectedFieldData()!.distribution![1] > 0" 
                     class="boolean-bar false-bar" 
                     [style.height.%]="getDistributionHeight(getSelectedFieldData()!.distribution![1], getSelectedFieldData()!.distribution!)"
                     [matTooltip]="'False: ' + (getSelectedFieldData()!.distribution![1] | number) + ' values'">
                  <span *ngIf="!isBooleanBarShort(getSelectedFieldData()!.distribution![1], getSelectedFieldData()!.distribution!)" class="bar-label">F</span>
                  <span *ngIf="isBooleanBarShort(getSelectedFieldData()!.distribution![1], getSelectedFieldData()!.distribution!)" class="bar-label-above false-label">F</span>
                </div>
                <div *ngIf="getSelectedFieldData()!.distribution![2] > 0" 
                     class="boolean-bar null-bar" 
                     [style.height.%]="getDistributionHeight(getSelectedFieldData()!.distribution![2], getSelectedFieldData()!.distribution!)"
                     [matTooltip]="'Null: ' + (getSelectedFieldData()!.distribution![2] | number) + ' values'">
                  <span *ngIf="!isBooleanBarShort(getSelectedFieldData()!.distribution![2], getSelectedFieldData()!.distribution!)" class="bar-label">N</span>
                  <span *ngIf="isBooleanBarShort(getSelectedFieldData()!.distribution![2], getSelectedFieldData()!.distribution!)" class="bar-label-above null-label">N</span>
                </div>
              </div>
              
              <!-- Top Values (string columns only) -->
              <div *ngIf="isStringType(getSelectedFieldData()!.dataType) && getTopValueCounts(getSelectedFieldData()!.valueCounts).length > 0" class="top-values-chart">
                <div class="stats-section-title icon-text icon-text-sm">Top Values
                  <mat-icon svgIcon="info-circle" class="stat-tooltip-icon"
                    matTooltip="The most frequently occurring values in this column and their percentage of non-empty rows."
                    matTooltipPosition="above"></mat-icon>
                </div>
                <div class="top-values-list">
                  <div *ngFor="let item of getTopValueCounts(getSelectedFieldData()!.valueCounts)" class="top-value-item">
                    <span class="top-value-label" [matTooltip]="item.value" matTooltipPosition="left">{{ item.value }}</span>
                    <div class="top-value-bar-container">
                      <div class="top-value-bar" [style.width.%]="item.percentage"></div>
                    </div>
                    <span class="top-value-pct">{{ item.percentage.toFixed(1) }}%</span>
                  </div>
                </div>
              </div>
              
              <!-- Min/Max Range Labels for Numeric and DateTime -->
              <div *ngIf="isNumericType(getSelectedFieldData()!.dataType) || isDateTimeType(getSelectedFieldData()!.dataType)" class="distribution-range">
                <span class="range-min">{{ isDateTimeType(getSelectedFieldData()!.dataType) ? formatDateTimeValue(getSelectedFieldData()!.minimum) : getSelectedFieldData()!.minimum }}</span>
                <span class="range-max">{{ isDateTimeType(getSelectedFieldData()!.dataType) ? formatDateTimeValue(getSelectedFieldData()!.maximum) : getSelectedFieldData()!.maximum }}</span>
              </div>
            </div>
          </div>

          <!-- Column Information -->
          <div class="section">
            <div class="column-info">
              <div class="info-row">
                <span class="label">Data Type:</span>
                <span class="value">{{ getSelectedFieldData()?.dataType || 'N/A' }}</span>
              </div>
              <div class="info-row">
                <span class="label">Data Category:</span>
                <span class="value category-value">
                  <img 
                    class="data-category-icon-sidebar"
                    [src]="getDataCategoryIconRegular(getSelectedFieldData()?.dataCategory || '')"
                    [alt]="getDataCategoryDisplayName(getSelectedFieldData()?.dataCategory || '')"
                    [matTooltip]="getDataCategoryDisplayName(getSelectedFieldData()?.dataCategory || '')"
                    matTooltipPosition="right" />
                  {{ getDataCategoryDisplayName(getSelectedFieldData()?.dataCategory || '') || 'N/A' }}
                </span>
              </div>
              <ng-container *ngIf="getSelectedFieldData()?.assessmentProfile as profile">
                <div class="info-row">
                  <span class="label">Physical Type:</span>
                  <span class="value">{{ profile.physical_dtype }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Semantic Type:</span>
                  <span class="value">{{ profile.semantic_type }}</span>
                </div>
                <div class="info-row">
                  <span class="label">General Type:</span>
                  <span class="value">{{ profile.general_type }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Confidence:</span>
                  <span class="value">{{ (profile.confidence * 100) | number:'1.0-1' }}%</span>
                </div>
                <div class="info-row">
                  <span class="label">Nullable:</span>
                  <span class="value">{{ profile.is_nullable ? 'Yes' : 'No' }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Candidate Key:</span>
                  <span class="value">{{ profile.is_candidate_key ? 'Yes' : 'No' }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Unique Ratio:</span>
                  <span class="value">{{ (profile.unique_ratio * 100) | number:'1.0-1' }}%</span>
                </div>
              </ng-container>

              <!-- Missing Values Distribution -->
              <div class="missing-values-chart" *ngIf="getSelectedFieldData()?.missingAnalysis?.bins && getSelectedFieldData()!.missingAnalysis!.bins.length > 0">
                <div class="chart-header">
                  <span class="chart-title">Missing Values Distribution</span>
                </div>
                <div class="chart-bars">
                  <div class="bar-item" *ngFor="let bin of getSelectedFieldData()!.missingAnalysis!.bins">
                    <div class="bar-wrapper"
                         [matTooltip]="'Rows ' + bin.start_idx + '-' + bin.end_idx + ': ' + bin.missing_count + ' missing (' + (bin.missing_ratio * 100).toFixed(1) + '%)'">
                      <div class="bar bar-base" [class.has-missing]="bin.missing_ratio > 0"></div>
                      <div class="bar bar-overlay" 
                           [style.height.%]="bin.missing_ratio * 100">
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="info-row">
                <span class="label">Missing:</span>
                <span class="value">{{ getSelectedFieldData()?.missing || '0' }} ({{ (getSelectedFieldData()?.nullPercentage || 0).toFixed(2) }}%)</span>
              </div>
              <div class="info-row">
                <span class="label">Distinct:</span>
                <span class="value">{{ getSelectedFieldData()?.unique || '0' }} ({{ (getSelectedFieldData()?.distinctPercentage || 0).toFixed(2) }}%)</span>
              </div>
            </div>
          </div>

          <!-- Statistics Box Plot -->
          <div class="section" *ngIf="hasBoxPlotData(getSelectedFieldData())">
            <div class="boxplot-container">
              <!-- Box Plot for Numeric and DateTime Columns -->
              <div class="box-plot">
                <div class="box-plot-chart">
                  <div class="box-plot-scale">
                    <!-- Whisker line (min to max) -->
                    <div class="whisker-line"></div>
                    
                    <!-- Left whisker (min to Q1) -->
                    <div class="whisker left-whisker" 
                         [style.left.%]="0"
                         [style.width.%]="getBoxPlotPosition('q1')">
                      <div class="whisker-cap min-cap"></div>
                    </div>
                    
                    <!-- Box (Q1 to Q3) -->
                    <div class="box" 
                         [style.left.%]="getBoxPlotPosition('q1')"
                         [style.width.%]="getBoxPlotPosition('q3') - getBoxPlotPosition('q1')">
                      <!-- Q1 hover trigger (left edge of box) -->
                      <div class="q1-trigger"></div>
                      <!-- Median line -->
                      <div class="median-line" [style.left.%]="getMedianPosition()"></div>
                      <!-- Q3 hover trigger (right edge of box) -->
                      <div class="q3-trigger"></div>
                    </div>
                    
                    <!-- Right whisker (Q3 to max) -->
                    <div class="whisker right-whisker" 
                         [style.left.%]="getBoxPlotPosition('q3')"
                         [style.width.%]="100 - getBoxPlotPosition('q3')">
                      <div class="whisker-cap max-cap"></div>
                    </div>
                  </div>
                  
                  <!-- Box plot labels -->
                  <div class="box-plot-labels">
                    <span class="label min-label" [style.left.%]="0">
                      {{ isDateTimeType(getSelectedFieldData()!.dataType) 
                         ? formatBoxPlotDateTimeValue(getSelectedFieldData()?.minimum) 
                         : getSelectedFieldData()?.minimum }}
                    </span>
                    <span *ngIf="!isDateTimeType(getSelectedFieldData()!.dataType)" 
                          class="label q1-label" 
                          [style.left.%]="getBoxPlotPosition('q1')">{{ getSelectedFieldData()?.percentile25 }}</span>
                    <span class="label median-label" [style.left.%]="getBoxPlotPosition('median')">
                      {{ isDateTimeType(getSelectedFieldData()!.dataType) 
                         ? formatBoxPlotDateTimeValue(getSelectedFieldData()?.median) 
                         : getSelectedFieldData()?.median }}
                    </span>
                    <span *ngIf="!isDateTimeType(getSelectedFieldData()!.dataType)" 
                          class="label q3-label" 
                          [style.left.%]="getBoxPlotPosition('q3')">{{ getSelectedFieldData()?.percentile75 }}</span>
                    <span class="label max-label" [style.left.%]="100">
                      {{ isDateTimeType(getSelectedFieldData()!.dataType) 
                         ? formatBoxPlotDateTimeValue(getSelectedFieldData()?.maximum) 
                         : getSelectedFieldData()?.maximum }}
                    </span>
                  </div>
                </div>
              </div>
              
              <!-- Separator line for non-numeric and non-datetime columns -->
              <div *ngIf="!getSelectedFieldData() || !isIQRApplicable(getSelectedFieldData()!.dataType)" class="section-separator">
                <div class="separator-line"></div>
              </div>
            </div>
          </div>

          <!-- Detailed Statistics -->
          <div class="section">
            <div class="detailed-stats">
              <div class="stats-grid">
                <div *ngIf="getSelectedFieldData() && isStatisticApplicable('min', getSelectedFieldData()!.dataType)" class="stat-item">
                  <span class="stat-label">Minimum:</span>
                  <span class="stat-value">{{ formatNumericValue(getSelectedFieldData()?.minimum) }}</span>
                </div>
                <div *ngIf="getSelectedFieldData() && isStatisticApplicable('q1', getSelectedFieldData()!.dataType)" class="stat-item">
                  <span class="stat-label icon-text icon-text-sm">25<sup>th</sup> Percentile:
                    <mat-icon svgIcon="info-circle" class="stat-tooltip-icon"
                      matTooltip="The value below which 25% of the data falls. Part of the interquartile range (IQR) used to measure data spread."
                      matTooltipPosition="above"></mat-icon>
                  </span>
                  <span class="stat-value">{{ formatNumericValue(getSelectedFieldData()?.percentile25) }}</span>
                </div>
                <div *ngIf="getSelectedFieldData() && isStatisticApplicable('median', getSelectedFieldData()!.dataType)" class="stat-item">
                  <span class="stat-label icon-text icon-text-sm">Median:
                    <mat-icon svgIcon="info-circle" class="stat-tooltip-icon"
                      matTooltip="The middle value when all data points are sorted in order. Half the values fall above and half below this point."
                      matTooltipPosition="above"></mat-icon>
                  </span>
                  <span class="stat-value">{{ formatNumericValue(getSelectedFieldData()?.median) }}</span>
                </div>
                <div *ngIf="getSelectedFieldData() && isStatisticApplicable('q3', getSelectedFieldData()!.dataType)" class="stat-item">
                  <span class="stat-label icon-text icon-text-sm">75<sup>th</sup> Percentile:
                    <mat-icon svgIcon="info-circle" class="stat-tooltip-icon"
                      matTooltip="The value below which 75% of the data falls. Part of the interquartile range (IQR) used to measure data spread."
                      matTooltipPosition="above"></mat-icon>
                  </span>
                  <span class="stat-value">{{ formatNumericValue(getSelectedFieldData()?.percentile75) }}</span>
                </div>
                <div *ngIf="getSelectedFieldData() && isStatisticApplicable('max', getSelectedFieldData()!.dataType)" class="stat-item">
                  <span class="stat-label">Maximum:</span>
                  <span class="stat-value">{{ formatNumericValue(getSelectedFieldData()?.maximum) }}</span>
                </div>
              </div>
              
              <div class="stats-grid secondary">
                <div *ngIf="getSelectedFieldData() && isStatisticApplicable('sum', getSelectedFieldData()!.dataType)" class="stat-item">
                  <span class="stat-label">Sum:</span>
                  <span class="stat-value">{{ formatNumericValue(getSelectedFieldData()?.sum) }}</span>
                </div>
                <div *ngIf="getSelectedFieldData() && isStatisticApplicable('mean', getSelectedFieldData()!.dataType)" class="stat-item">
                  <span class="stat-label icon-text icon-text-sm">Mean:
                    <mat-icon svgIcon="info-circle" class="stat-tooltip-icon"
                      matTooltip="The arithmetic average: the sum of all values divided by the number of data points."
                      matTooltipPosition="above"></mat-icon>
                  </span>
                  <span class="stat-value">{{ formatNumericValue(getSelectedFieldData()?.mean) }}</span>
                </div>
                <div *ngIf="getSelectedFieldData() && isStatisticApplicable('mode', getSelectedFieldData()!.dataType)" class="stat-item">
                  <span class="stat-label icon-text icon-text-sm">Mode:
                    <mat-icon svgIcon="info-circle" class="stat-tooltip-icon"
                      matTooltip="The most frequently occurring value in the column. A column may have multiple modes."
                      matTooltipPosition="above"></mat-icon>
                  </span>
                  <span class="stat-value">{{ formatNumericValue(getSelectedFieldData()?.mode) }}</span>
                </div>
                <div *ngIf="getSelectedFieldData() && isStatisticApplicable('standardDeviation', getSelectedFieldData()!.dataType)" class="stat-item">
                  <span class="stat-label icon-text icon-text-sm">Standard Deviation:
                    <mat-icon svgIcon="info-circle" class="stat-tooltip-icon"
                      matTooltip="Measures how spread out values are from the mean. A higher value indicates greater variability in the data."
                      matTooltipPosition="above"></mat-icon>
                  </span>
                  <span class="stat-value">{{ formatNumericValue(getSelectedFieldData()?.stdDev) }}</span>
                </div>
                <div *ngIf="getSelectedFieldData() && isStringType(getSelectedFieldData()!.dataType) && getSelectedFieldData()!.minLength !== undefined && getSelectedFieldData()!.minLength !== null" class="stat-item">
                  <span class="stat-label icon-text icon-text-sm">Min Length:
                    <mat-icon svgIcon="info-circle" class="stat-tooltip-icon"
                      matTooltip="The shortest string value in this column, measured in characters."
                      matTooltipPosition="above"></mat-icon>
                  </span>
                  <span class="stat-value">{{ getSelectedFieldData()!.minLength }}</span>
                </div>
                <div *ngIf="getSelectedFieldData() && isStringType(getSelectedFieldData()!.dataType) && getSelectedFieldData()!.maxLength !== undefined && getSelectedFieldData()!.maxLength !== null" class="stat-item">
                  <span class="stat-label icon-text icon-text-sm">Max Length:
                    <mat-icon svgIcon="info-circle" class="stat-tooltip-icon"
                      matTooltip="The longest string value in this column, measured in characters."
                      matTooltipPosition="above"></mat-icon>
                  </span>
                  <span class="stat-value">{{ getSelectedFieldData()!.maxLength }}</span>
                </div>
                <div *ngIf="getSelectedFieldData() && isStringType(getSelectedFieldData()!.dataType) && getSelectedFieldData()!.mean !== 'N/A'" class="stat-item">
                  <span class="stat-label icon-text icon-text-sm">Avg Length:
                    <mat-icon svgIcon="info-circle" class="stat-tooltip-icon"
                      matTooltip="The average (mean) number of characters across all non-null values in this column."
                      matTooltipPosition="above"></mat-icon>
                  </span>
                  <span class="stat-value">{{ formatNumericValue(getSelectedFieldData()!.mean) }}</span>
                </div>
                <div *ngIf="getSelectedFieldData() && isStringType(getSelectedFieldData()!.dataType) && getSelectedFieldData()!.median !== 'N/A'" class="stat-item">
                  <span class="stat-label icon-text icon-text-sm">Median Length:
                    <mat-icon svgIcon="info-circle" class="stat-tooltip-icon"
                      matTooltip="The median number of characters across all non-null values. Half the values are shorter, half are longer."
                      matTooltipPosition="above"></mat-icon>
                  </span>
                  <span class="stat-value">{{ formatNumericValue(getSelectedFieldData()!.median) }}</span>
                </div>
              </div>
            </div>
          </div>


        </div>
      </div>

    </div>
  </div>

</div>
